import { useState } from 'react';
import { observer } from 'mobx-react-lite';

import { Checkbox, CopyButton, DataView } from 'components';
import { useFollow } from 'hooks/meme/useFollow';
import { useUnfollow } from 'hooks/meme/useUnfollow';
import { store } from 'store';
import { cutAddress } from 'utils/helpers';
import { Address, isAddress } from 'viem';

import * as S from '../common.styles';

const PROFILE_BASE_URL = 'https://wowmax-polymarket.pages.dev/copy-trading/137';

const parseLeaderAddresses = (value: string) =>
  Array.from(new Set(value.match(/0x[a-fA-F0-9]{40}/g) || []));

export const CopiedWallets = observer(() => {
  const { wallets, portfolioResponse } = store.portfolioStore;
  const isLoading = portfolioResponse.isLoading && !portfolioResponse.data;

  const follow = useFollow();
  const unfollow = useUnfollow();

  const [selectedWallets, setSelectedWallets] = useState<Set<string>>(new Set());
  const [newWalletAddress, setNewWalletAddress] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const leadersToUnfollowAll = wallets
    .filter((w) => selectedWallets.has(w.address))
    .map((l) => l.address);

  const handleUnfollowClick = async (leaders: Address[]) => {
    await unfollow(leaders, false);
  };

  const handleAddWallet = async () => {
    const parsedAddresses = parseLeaderAddresses(newWalletAddress);
    if (!parsedAddresses.length || !parsedAddresses.every((address) => isAddress(address))) return;

    setIsAdding(true);
    const success = await follow(parsedAddresses as Address[]);
    if (success) setNewWalletAddress('');
    setIsAdding(false);
  };

  const parsedAddresses = parseLeaderAddresses(newWalletAddress);
  const hasInput = parsedAddresses.length > 0;
  const isValidAddress = hasInput && parsedAddresses.every((address) => isAddress(address));

  const isAllSelected = wallets.length > 0 && selectedWallets.size === wallets.length;
  const isPartialSelected = selectedWallets.size > 0 && selectedWallets.size < wallets.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedWallets(new Set());
    } else {
      setSelectedWallets(new Set(wallets.map((w) => w.address)));
    }
  };

  const handleSelectWallet = (leader: string) => {
    setSelectedWallets((prev) => {
      const next = new Set(prev);
      if (next.has(leader)) {
        next.delete(leader);
      } else {
        next.add(leader);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <S.EmptyState>
        <div>LOADING...</div>
        <div>Loading wallets...</div>
      </S.EmptyState>
    );
  }

  if (wallets.length === 0) {
    return (
      <div>
        <S.AddWalletBar>
          <S.AddWalletInput
            type="text"
            placeholder="Paste leader wallets"
            value={newWalletAddress}
            onChange={(e) => setNewWalletAddress(e.target.value)}
          />
          <S.BlueButton onClick={handleAddWallet} disabled={!isValidAddress || isAdding}>
            {isAdding ? '...' : 'Add'}
          </S.BlueButton>
          <S.FindButton href={PROFILE_BASE_URL} target="_blank" rel="noopener noreferrer">
            Find Traders
          </S.FindButton>
        </S.AddWalletBar>
        <S.EmptyState>
          <div>// NO DATA</div>
          <div>No copied wallets</div>
        </S.EmptyState>
      </div>
    );
  }

  return (
    <div>
      <S.AddWalletBar>
        {selectedWallets.size > 0 && (
          <S.AddWalletLeft>
            <S.SelectedCount>{selectedWallets.size} selected</S.SelectedCount>
            {isPartialSelected && (
              <S.SelectAllText onClick={handleSelectAll}>
                Select all {wallets.length}
              </S.SelectAllText>
            )}
            <S.BulkActionButton
              $variant="danger"
              onClick={() => handleUnfollowClick([...selectedWallets] as Address[])}
              disabled={leadersToUnfollowAll.length === 0}
            >
              Unfollow ({leadersToUnfollowAll.length})
            </S.BulkActionButton>
          </S.AddWalletLeft>
        )}
        <S.AddWalletRight>
          <S.AddWalletInput
            type="text"
            placeholder="Paste leader wallets"
            value={newWalletAddress}
            onChange={(e) => setNewWalletAddress(e.target.value)}
          />
          <S.BlueButton
            size="small"
            onClick={handleAddWallet}
            disabled={!isValidAddress || isAdding}
          >
            {isAdding ? '...' : 'Add'}
          </S.BlueButton>
          <S.FindButton href={PROFILE_BASE_URL} target="_blank" rel="noopener noreferrer">
            Find Traders
          </S.FindButton>
        </S.AddWalletRight>
      </S.AddWalletBar>

      <S.DesktopOnlyTableWrapper>
        <S.Table>
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <Checkbox checked={isAllSelected} onChange={handleSelectAll} />
              </th>
              <th style={{ width: '50px' }}>#</th>
              <th>Leader</th>
              <th style={{ textAlign: 'center' }}>Status</th>
              <th style={{ textAlign: 'right' }}>Exposure</th>
              <th style={{ textAlign: 'right' }}>PnL</th>
              <th style={{ textAlign: 'right' }}>ROI</th>
              <th style={{ textAlign: 'center' }}>Positions</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {wallets.map((wallet, index) => {
              const isSelected = selectedWallets.has(wallet.address);
              const alias = wallet.name || wallet.pseudonym;
              const leaderIcon = wallet.profileImageOptimized || wallet.profileImage;

              return (
                <S.SelectableTr
                  key={wallet.address}
                  $isSelected={isSelected}
                  $isPaused={!wallet.enabled}
                >
                  <td>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectWallet(wallet.address)}
                    />
                  </td>
                  <td>
                    <S.RankBadge $rank={index + 1}>{index + 1}</S.RankBadge>
                  </td>
                  <td>
                    <S.Address>
                      {leaderIcon && (
                        <S.EntityIcon src={leaderIcon} alt="" loading="lazy" />
                      )}
                      <S.AddressLink
                        href={`https://polygonscan.com/address/${wallet.address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {cutAddress(wallet.address)}
                      </S.AddressLink>
                      <CopyButton copyText={wallet.address} />
                      {alias && <S.PausedBadge>{alias}</S.PausedBadge>}
                    </S.Address>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <S.TokensBadge>{wallet.enabled ? 'Active' : 'Paused'}</S.TokensBadge>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <DataView value={wallet.totalCurrentValue} type="dollars" toFixed={2} />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <DataView value={wallet.totalPnl} hasSign hasColor type="dollars" toFixed={2} />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <DataView value={wallet.roi} hasColor hasSign type="percent" toFixed={1} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {wallet.positionsCount > 0 ? (
                      <S.TokensBadge>{wallet.positionsCount} open</S.TokensBadge>
                    ) : (
                      <span style={{ color: 'rgba(120, 160, 190, 0.6)', fontSize: '12px' }}>—</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <S.ActionButton
                      $variant="danger"
                      onClick={() => handleUnfollowClick([wallet.address as Address])}
                    >
                      Stop copying
                    </S.ActionButton>
                  </td>
                </S.SelectableTr>
              );
            })}
          </tbody>
        </S.Table>
      </S.DesktopOnlyTableWrapper>

      <S.MobileCards>
        {wallets.map((wallet, index) => {
          const isSelected = selectedWallets.has(wallet.address);
          const alias = wallet.name || wallet.pseudonym;
          const leaderIcon = wallet.profileImageOptimized || wallet.profileImage;

          return (
            <S.MobileCard key={wallet.address}>
              <S.MobileCardHeader>
                <S.MobileCardTitle>
                  <S.Address>
                    <S.RankBadge $rank={index + 1}>{index + 1}</S.RankBadge>
                    {leaderIcon && (
                      <S.EntityIcon src={leaderIcon} alt="" loading="lazy" />
                    )}
                    <S.AddressLink
                      href={`https://polygonscan.com/address/${wallet.address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {cutAddress(wallet.address)}
                    </S.AddressLink>
                    <CopyButton copyText={wallet.address} />
                  </S.Address>
                </S.MobileCardTitle>
                {alias && <S.MobileCardSubtitle>{alias}</S.MobileCardSubtitle>}
              </S.MobileCardHeader>

              <S.MobileStatsGrid>
                <S.MobileStat>
                  <S.MobileStatLabel>Status</S.MobileStatLabel>
                  <S.MobileStatValue>
                    <S.TokensBadge>{wallet.enabled ? 'Active' : 'Paused'}</S.TokensBadge>
                  </S.MobileStatValue>
                </S.MobileStat>
                <S.MobileStat>
                  <S.MobileStatLabel>Exposure</S.MobileStatLabel>
                  <S.MobileStatValue>
                    <DataView value={wallet.totalCurrentValue} type="dollars" toFixed={2} />
                  </S.MobileStatValue>
                </S.MobileStat>
                <S.MobileStat>
                  <S.MobileStatLabel>PnL</S.MobileStatLabel>
                  <S.MobileStatValue>
                    <DataView value={wallet.totalPnl} hasSign hasColor type="dollars" toFixed={2} />
                  </S.MobileStatValue>
                </S.MobileStat>
                <S.MobileStat>
                  <S.MobileStatLabel>ROI</S.MobileStatLabel>
                  <S.MobileStatValue>
                    <DataView value={wallet.roi} hasColor hasSign type="percent" toFixed={1} />
                  </S.MobileStatValue>
                </S.MobileStat>
                <S.MobileStat>
                  <S.MobileStatLabel>Positions</S.MobileStatLabel>
                  <S.MobileStatValue>
                    {wallet.positionsCount > 0 ? (
                      <S.TokensBadge>{wallet.positionsCount} open</S.TokensBadge>
                    ) : (
                      '—'
                    )}
                  </S.MobileStatValue>
                </S.MobileStat>
                <S.MobileStat>
                  <S.MobileStatLabel>Select</S.MobileStatLabel>
                  <S.MobileStatValue>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectWallet(wallet.address)}
                    />
                  </S.MobileStatValue>
                </S.MobileStat>
              </S.MobileStatsGrid>

              <S.ActionButton
                $variant="danger"
                onClick={() => handleUnfollowClick([wallet.address as Address])}
              >
                Stop copying
              </S.ActionButton>
            </S.MobileCard>
          );
        })}
      </S.MobileCards>
    </div>
  );
});
